import { useMemo, useRef, useState } from "react";
import {
  Upload,
  AlertCircle,
  CheckCircle2,
  Loader2,
  X,
  FileSpreadsheet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

export interface ImportUploadZoneProps {
  onFileSelected: (file: File) => void;
  onUpload?: (file: File) => void;
  isLoading?: boolean;
  error?: string;
  success?: string;
  disabled?: boolean;
  acceptedFileExtensions?: string[];
  acceptedTypesHint?: string;
  title?: string;
  description?: string;
  loadingTitle?: string;
  loadingDescription?: string;
  selectButtonLabel?: string;
  submitButtonLabel?: string;
  clearButtonLabel?: string;
  selectedFileLabel?: string;
  successTitle?: string;
  errorTitle?: string;
}

export function ImportUploadZone({
  onFileSelected,
  onUpload,
  isLoading = false,
  error,
  success,
  disabled = false,
  acceptedFileExtensions = [".xlsx", ".xlsm"],
  acceptedTypesHint,
  title = "Envie a planilha para validar o lote antes da importação",
  description = "Arraste o arquivo aqui ou selecione no seu computador.",
  loadingTitle = "Validando lote...",
  loadingDescription = "Estamos verificando o modelo, os campos e a consistência das informações.",
  selectButtonLabel = "Selecionar planilha",
  submitButtonLabel = "Validar lote",
  clearButtonLabel = "Trocar arquivo",
  selectedFileLabel = "Arquivo selecionado",
  successTitle = "Planilha pronta para a próxima etapa",
  errorTitle = "Não conseguimos conferir esta planilha",
}: ImportUploadZoneProps) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptedTypesHintText = useMemo(
    () =>
      acceptedTypesHint ??
      `Arquivos aceitos: ${acceptedFileExtensions.join(", ")}. Tamanho máximo: 5 MB.`,
    [acceptedFileExtensions, acceptedTypesHint],
  );

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    const normalizedName = file.name.toLowerCase();
    const normalizedExtensions = acceptedFileExtensions.map((item) =>
      item.toLowerCase(),
    );

    if (
      !normalizedExtensions.some((extension) => normalizedName.endsWith(extension))
    ) {
      return {
        valid: false,
        error: `Este formato de arquivo não é aceito. Envie uma planilha em ${acceptedFileExtensions.join(", ")}.`,
      };
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        valid: false,
        error:
          "O arquivo excede 5MB. Divida o lote ou reduza o conteúdo antes de reenviar.",
      };
    }

    return { valid: true };
  };

  const handleValidFile = (file: File) => {
    setLocalError(null);
    setSelectedFileName(file.name);
    setSelectedFile(file);
    onFileSelected(file);
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    }
    if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const validation = validateFile(file);
    if (!validation.valid) {
      setLocalError(validation.error || errorTitle);
      return;
    }

    handleValidFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const validation = validateFile(file);
    if (!validation.valid) {
      setLocalError(validation.error || errorTitle);
      return;
    }

    handleValidFile(file);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = () => {
    if (selectedFile && onUpload) {
      setLocalError(null);
      onUpload(selectedFile);
    }
  };

  const handleClear = () => {
    setSelectedFileName("");
    setSelectedFile(null);
    setLocalError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const effectiveError = localError || error;
  const hasCurrentFileAlreadyBeenChecked = Boolean(success && selectedFile);

  return (
    <div className="space-y-4">
      <Card
        className={`relative border-2 border-dashed transition-all ${
          isDragActive
            ? "border-blue-500 bg-blue-50/70"
            : "border-slate-300 hover:border-blue-300 hover:bg-slate-50/80"
        } ${isLoading || disabled ? "pointer-events-none opacity-60" : ""}`}
        onDragEnter={disabled ? undefined : handleDrag}
        onDragLeave={disabled ? undefined : handleDrag}
        onDragOver={disabled ? undefined : handleDrag}
        onDrop={disabled ? undefined : handleDrop}
      >
        <div className="flex flex-col items-center justify-center gap-4 p-8 text-center md:p-10">
          {isLoading ? (
            <>
              <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-slate-900">{loadingTitle}</p>
                <p className="text-sm text-slate-600">{loadingDescription}</p>
              </div>
            </>
          ) : (
            <>
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-700">
                <Upload className="h-7 w-7" />
              </div>
              <div className="space-y-1">
                <p className="text-base font-semibold text-slate-900">{title}</p>
                <p className="text-sm text-slate-600">{description}</p>
                <p className="text-sm text-slate-500">{acceptedTypesHintText}</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClick}
                disabled={isLoading || disabled}
              >
                {selectButtonLabel}
              </Button>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFileExtensions.join(",")}
          onChange={handleFileInput}
          className="hidden"
          disabled={isLoading || disabled}
        />
      </Card>

      {selectedFileName && !isLoading && (
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 p-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-blue-700 shadow-sm">
                <FileSpreadsheet className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-blue-700">
                  {selectedFileLabel}
                </p>
                <p className="truncate text-sm font-semibold text-blue-950">
                  {selectedFileName}
                </p>
              </div>
            </div>
            <button
              onClick={handleClear}
              className="rounded-md p-1 text-blue-600 transition-colors hover:bg-blue-100 hover:text-blue-900"
              title="Remover arquivo"
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleUpload}
              disabled={isLoading || !selectedFile || hasCurrentFileAlreadyBeenChecked}
              className="flex-1"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {loadingTitle}
                </>
              ) : (
                submitButtonLabel
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClear}
              disabled={isLoading}
            >
              {clearButtonLabel}
            </Button>
          </div>
        </div>
      )}

      {effectiveError && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
          <div>
            <p className="text-sm font-semibold text-red-900">{errorTitle}</p>
            <p className="mt-1 text-sm text-red-700">{effectiveError}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
          <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
          <div>
            <p className="text-sm font-semibold text-green-900">{successTitle}</p>
            <p className="mt-1 text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}
    </div>
  );
}
